import { Injectable, Injector, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AutoLogoutService } from './auto-logout.service';

/**
 * sessionStorage key holding the reason a tab was logged out by *another* tab,
 * so the sign-in page can explain why the user suddenly landed there.
 */
export const SESSION_LOGOUT_NOTICE_KEY = 'sessionLogoutNotice';

const LOGOUT_CHANNEL = 'hippo-session';
const LOGOUT_MESSAGE = 'logout';

/**
 * localStorage key used as a second signal path: `storage` events fire in every
 * other tab of the same origin, which also covers browsers without
 * BroadcastChannel support.
 */
const LOGOUT_SIGNAL_KEY = 'hippoLogoutSignal';

/**
 * Keeps the login state in sync across all browser tabs of the same origin.
 *
 * The logged-in user lives in `sessionStorage`, which is per-tab — so signing
 * out in one tab used to leave every other tab happily logged in: the PHP
 * session and (for Longines-ID / Cognito logins) the OIDC session were gone,
 * but the other tab still had its own copy of the user and kept navigating
 * (HIP-40). This service broadcasts the sign-out so the remaining tabs drop
 * their session immediately instead of on their next failing request.
 */
@Injectable({ providedIn: 'root' })
export class SessionSyncService {
    private readonly router = inject(Router);
    private readonly zone = inject(NgZone);
    private readonly oidc = inject(OidcSecurityService);
    private readonly injector = inject(Injector);

    private channel: BroadcastChannel | null = null;
    private listening = false;

    /**
     * Starts listening for sign-outs from other tabs. Called once on app start;
     * repeated calls are ignored.
     */
    start(): void {
        if (this.listening || typeof window === 'undefined') {
            return;
        }
        this.listening = true;

        if (typeof BroadcastChannel !== 'undefined') {
            try {
                this.channel = new BroadcastChannel(LOGOUT_CHANNEL);
                this.channel.onmessage = event => {
                    if (event?.data === LOGOUT_MESSAGE) {
                        this.onRemoteLogout();
                    }
                };
            } catch {}
        }

        window.addEventListener('storage', event => {
            if (event.key === LOGOUT_SIGNAL_KEY && event.newValue) {
                this.onRemoteLogout();
            }
        });
    }

    /**
     * Signals every other tab of this browser to end its session. Must be
     * called *before* a logout redirect navigates this tab away.
     */
    broadcastLogout(): void {
        if (typeof window === 'undefined') {
            return;
        }
        try {
            this.channel?.postMessage(LOGOUT_MESSAGE);
        } catch {}
        try {
            // The value has to change on every logout, otherwise the `storage`
            // event does not fire in the other tabs.
            window.localStorage.setItem(
                LOGOUT_SIGNAL_KEY,
                `${Date.now()}-${Math.random()}`
            );
        } catch {}
    }

    /**
     * Another tab signed out: drop this tab's session too and send it to the
     * login screen. Both signal paths can arrive, so this is a no-op once the
     * local session is already gone.
     */
    private onRemoteLogout(): void {
        if (!sessionStorage.getItem('user')) {
            return;
        }
        this.zone.run(() => {
            // Lazy lookup: AutoLogoutService pulls in TranslocoService, whose
            // http loader ends up back at the auth services — injecting it in a
            // field would make that a circular dependency.
            try {
                this.injector.get(AutoLogoutService).clearTimer();
            } catch {}
            // Drop the OIDC token store as well, otherwise the memoized Cognito
            // handshake could re-exchange this tab's still-cached token and log
            // the user straight back in (same failure mode as HIP-33).
            try {
                this.oidc.logoffLocal();
            } catch {}
            try {
                sessionStorage.clear();
            } catch {}
            try {
                sessionStorage.setItem(
                    SESSION_LOGOUT_NOTICE_KEY,
                    this.logoutNotice()
                );
            } catch {}
            this.router.navigate(['/sign-in']);
        });
    }

    private logoutNotice(): string {
        let lang = 'de';
        try {
            const transloco = this.injector.get(TranslocoService);
            lang = transloco.getActiveLang() || transloco.getDefaultLang();
        } catch {}
        return lang === 'en'
            ? 'You have been logged out in another tab.'
            : 'Sie wurden in einem anderen Tab abgemeldet.';
    }
}
